/**
 * The typed `/api/steam/*` client shared by both repositories.
 *
 * Extracted verbatim from `LocalRepository` in Phase 4 (deviation P4-01): the
 * Worker proxy is the *same* upstream for `SupabaseRepository`, and the row →
 * `Game` / appdetails → `SteamFactPatch` mappings are transcriptions of the
 * prototype (lines 1639 and 1655) that must not drift between the two
 * implementations. Behaviour is unchanged; only the file it lives in moved.
 *
 * ARCHITECTURE §4.3: `stale: true` is a failure for freshness purposes — the
 * data is still returned to the caller by the repository's fallback path, but
 * `lastOkAt` must not advance, so `api()` throws on it.
 */

import { WRITEBACK_COLUMNS } from '../../../shared/cardFields.ts';
import { MO } from '../lib/format.ts';
import type {
  ApiResponse,
  Game,
  GameMedia,
  SteamFactPatch,
  SteamMovie,
  SteamShot,
} from '../lib/types.ts';
import { RepoError } from './Repository.ts';
import type { BrowseQuery } from './Repository.ts';

export interface SteamApiOptions {
  /** Base for `/api/steam/*`. Empty string = same origin (the Vite proxy). */
  apiBase?: string;
  fetchImpl?: typeof fetch | null;
}

/** One `/api/steam/*` round trip, envelope unwrapped (ARCHITECTURE §4.3). */
export async function steamApi<T>(
  opts: SteamApiOptions,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const f = opts.fetchImpl;
  if (!f) throw new RepoError('OFFLINE');
  const res = await f((opts.apiBase ?? '') + path, init);
  const body = (await res.json()) as ApiResponse<T>;
  if (!body || body.ok !== true) {
    throw new RepoError(body && body.ok === false ? body.code : 'INTERNAL');
  }
  if (body.stale) throw new RepoError('UPSTREAM_STATUS', 'stale');
  return body.data;
}

export const normName = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');

const rec = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;

/**
 * The stand-in description a `storesearch` row carries until something real
 * arrives. A NAMED CONSTANT because two files have to agree on it: this one
 * writes it, and `slices/steamFacts.ts#applyFact` treats it as a hole that a
 * Steam short description may overwrite. Inline in both places, the day the
 * wording changed the overwrite would silently stop happening.
 */
export const LIVE_ROW_DESC = 'Live Steam search result — open it and full details load from the store page.';

/** One raw `storesearch` row → the prototype's live-row shape (line 1639). */
export function toLiveGame(row: unknown): Game | null {
  const r = rec(row);
  if (!r) return null;
  const id = typeof r['id'] === 'number' ? r['id'] : Number(r['id']);
  if (!Number.isSafeInteger(id) || id <= 0) return null;
  const price = rec(r['price']);
  const final = typeof price?.['final'] === 'number' ? (price['final'] as number) : null;
  const initial = typeof price?.['initial'] === 'number' ? (price['initial'] as number) : 0;
  return {
    id,
    appid: id,
    n: typeof r['name'] === 'string' ? r['name'] : 'Steam App #' + id,
    dev: '—',
    rd: '',
    p: final === null ? null : final / 100,
    o: final !== null && initial > final ? initial / 100 : 0,
    mn: 0,
    mx: 0,
    plat: 'P',
    tags: [],
    rp: 0,
    pk: 0,
    ccu: 0,
    up: false,
    ds: LIVE_ROW_DESC,
    ea: false,
    demo: false,
    dAid: 0,
    art: '',
    live: true,
    thumb: typeof r['tiny_image'] === 'string' ? r['tiny_image'] : '',
  };
}

/**
 * One `/api/steam/browse` row → a `Game`, plus its art.
 *
 * Browse rows are **`live` rows**. That is the whole trick: everything that
 * already resolves, renders, art-loads, Detail-opens and quick-adds a live
 * search row does so for a browse row with no changes at all, because it IS one.
 *
 * What is deliberately NOT carried across:
 *
 *   `tags`    stays empty. Steam's `tagids` are numeric ids from a 446-word
 *             vocabulary; `Game.tags` is the crew's own editorial word list,
 *             compared by exact string. Mixing the two namespaces would
 *             silently break every editorial-tag comparison. The ids ride on
 *             `tids` instead — the one field DEFINED to be filtered on, in
 *             their own vocabulary, so the exclude predicate compares ids to
 *             ids and the namespaces still never meet.
 *   `mn`/`mx` stay 0 — Steam reports co-op and multiplayer CATEGORIES but never
 *             a player count. 0 is the "we were not told" sentinel that
 *             `knownOf` reads (DISCOVERY decision 5); the schema default of 1
 *             would be a claim, and a false one. The categories themselves DO
 *             come across — see `pcats` below.
 *   `p`/`o`   stay null/0. Price is volatile and belongs to the self-healing
 *             facts path (DISCOVERY §6), not to a page cached for ten minutes.
 *             `store/slices/browse.ts` re-applies whatever this session has
 *             already healed for the appid, so the omission costs no price the
 *             client actually knows.
 *
 * `pcats` is the one player-facing fact GetItems does answer, and the reason
 * `mx: 0` is not the end of the story: `playersTxt` reads
 * `supported_player_categoryids` for the coarse CO-OP / MULTI / SOLO label
 * whenever `mx` is the unknown sentinel — which on a browse row it always is —
 * so a tile stops rendering an em dash for a game whose store page says "Online
 * Co-op" in plain sight. DISPLAY ONLY, exactly like `stags`: `knownOf` and
 * `passFlt` keep reading the raw `mn`/`mx` sentinels, so nothing here can
 * narrow a filter.
 */
/**
 * `BrowseQuery` → the `/api/steam/browse` query string, for BOTH repositories.
 *
 * Shared rather than written twice because the two copies had already been
 * kept in step by hand across `term`/`tags`/`start`, and the sort and the
 * narrowing are exactly the kind of addition one copy gets and the other does
 * not. A missed field here does not fail: the Worker would answer an
 * un-narrowed page, the client would caption it with the narrowed heading, and
 * only the row count would look faintly wrong.
 *
 * Absent means not asked, so a default question serializes byte-identically to
 * the string this route has always received — which is also what keeps the
 * un-narrowed answer on its existing cache entry.
 */
export function browseParams(q: BrowseQuery): URLSearchParams {
  const p = new URLSearchParams();
  if (q.term !== '') p.set('term', q.term);
  if (q.tags.length > 0) p.set('tags', q.tags.join(','));
  if (q.start > 0) p.set('start', String(q.start));
  if (q.sort !== null) p.set('sort', q.sort);
  if (q.maxPrice !== null) p.set('maxprice', String(q.maxPrice));
  if (q.specials) p.set('specials', '1');
  if (q.comingSoon) p.set('comingsoon', '1');
  return p;
}

export function toBrowseGame(row: unknown): { game: Game; lib: string; hdr: string } | null {
  const r = rec(row);
  if (!r) return null;
  const appid = typeof r['appid'] === 'number' ? r['appid'] : Number(r['appid']);
  if (!Number.isSafeInteger(appid) || appid <= 0) return null;
  const name = typeof r['name'] === 'string' ? r['name'] : '';
  if (name === '') return null;

  const pct = Number(r['reviewPct']);
  // Steam's rung, which the worker has always had on the wire and this mapper
  // ignored until 2026-08-27. It is the review value that knows how MANY
  // reviews there are, so it — not `rp` — is what the review filter reads.
  const rung = Number(r['reviewScore']);
  const game: Game = {
    id: appid,
    appid,
    n: name,
    dev: typeof r['dev'] === 'string' && r['dev'] !== '' ? (r['dev'] as string) : '—',
    rd: '',
    p: null,
    o: 0,
    mn: 0,
    mx: 0,
    plat: 'P',
    tags: [],
    rp: Number.isFinite(pct) && pct > 0 ? pct : 0,
    ...(Number.isInteger(rung) && rung >= 1 && rung <= 9 ? { rs: rung } : {}),
    pk: 0,
    ccu: 0,
    up: r['up'] === true,
    ds: '',
    ea: r['ea'] === true,
    demo: r['demo'] === true,
    dAid: typeof r['dAid'] === 'number' ? r['dAid'] : 0,
    art: '',
    live: true,
    thumb: '',
  };

  // THE RELEASE-STATE FLAGS WERE ANSWERED — when they were.
  //
  // `up` and `ea` are read as `=== true` just above, which collapses "Steam
  // said false" and "Steam said nothing" into the same `false`. That collapse is
  // right for the FLAG (a ribbon must not draw on silence) and wrong for the
  // FILTER, which needs to tell the two apart: `knownOf` reads `live: true` as
  // "release state unknown", so without this marker the RELEASED / UPCOMING /
  // EARLY ACCESS tri-states passed every row of every tag browse untouched.
  //
  // `worker/src/steam.ts#itemFlags` emits both keys, as real booleans, for every
  // resolved and LISTED item, and omits both for an unlisted one — where proto3
  // silence stops meaning false. So the presence of the key IS the answer to
  // "was this answered", and it is tested rather than the value.
  if (typeof r['up'] === 'boolean' || typeof r['ea'] === 'boolean') game.stOk = true;

  // `worker/src/steam.ts#BrowseRow.playerCats`, already filtered to positive
  // safe integers there — re-checked here because this argument is `unknown`
  // and an upstream shape nobody in this file controls.
  //
  // ABSENT, never `[]`, when the row carried none: `pcats` distinguishes "not
  // answered" from "answered with nothing" by absence alone (lib/types.ts), and
  // an item whose payload had no `categories` block at all must not be recorded
  // as a game that has no multiplayer.
  const cats = (Array.isArray(r['playerCats']) ? (r['playerCats'] as unknown[]) : []).filter(
    (c): c is number => typeof c === 'number' && c > 0,
  );
  if (cats.length) game.pcats = cats;

  // `worker/src/steam.ts#BrowseRow.tagids`, same re-check-the-unknown treatment
  // as `playerCats` above — and the same ABSENT-never-`[]` rule, because for
  // `tids` absence is load-bearing the other way round: it is what lets the
  // exclude predicate tell "this row carries no excluded tag" from "this row
  // was never asked", and only the first may hide a row (decision 5).
  const tids = (Array.isArray(r['tagids']) ? (r['tagids'] as unknown[]) : []).filter(
    (t): t is number => typeof t === 'number' && Number.isSafeInteger(t) && t > 0,
  );
  if (tids.length) game.tids = tids;

  return {
    game,
    lib: typeof r['lib'] === 'string' ? r['lib'] : '',
    hdr: typeof r['hdr'] === 'string' ? r['hdr'] : '',
  };
}

/**
 * appdetails `data` → the volatile fact set, transcribed from the prototype's
 * `enrich()` (line 1655) including its month-abbreviation regex. Only the
 * volatile set is extracted: curated editorial fields are never written from a
 * Steam response (ARCHITECTURE §1.4 / decision D2).
 */
export function mapAppDetails(appid: number, d: Record<string, unknown>): SteamFactPatch {
  const out: SteamFactPatch = { appid };
  const rd = rec(d['release_date']);
  out.up = !!(rd && rd['coming_soon'] === true);

  const dateStr = typeof rd?.['date'] === 'string' ? (rd['date'] as string) : '';
  const m = /([A-Z][a-z]{2})[a-z]*,? (?:\d{1,2},? )?(\d{4})/.exec(dateStr);
  const mon = m && m[1] ? MO[m[1]] : undefined;
  if (m && mon) out.rd = m[2] + '-' + mon;

  // THE DAY, which this function used to match and throw away — the group above
  // is deliberately non-capturing so `rd` stays `YYYY-MM`, and four consumers
  // depend on that shape. It is captured separately instead.
  //
  // Steam emits BOTH orders under `cc=us&l=english` — `Oct 23, 2023` and
  // `23 Oct, 2023` — and a single pattern silently loses the day on roughly
  // half of titles, so there are two. Both REQUIRE a literal day, which is what
  // makes this path safe on unreleased games: `Coming soon`, `Q4 2026` and
  // `2026` match neither, so a title with no announced day gets no day here
  // rather than a fabricated one. That is precisely why the nightly pool writer
  // is NOT the source for this — it reads an epoch Steam fills with a
  // placeholder for coming-soon rows.
  //
  // Stored ISO (`2023-10-23`) rather than the display form the prototype keeps
  // on `rdFull`: this value travels to a `date` column and back, and formatting
  // belongs at the render edge where `fmtDFull` can localise or fall back.
  const dm = /([A-Z][a-z]{2})[a-z]*,? (\d{1,2}),? (\d{4})/.exec(dateStr);
  const md = /(\d{1,2}) ([A-Z][a-z]{2})[a-z]*,? (\d{4})/.exec(dateStr);
  const day = dm ? { mon: MO[dm[1] ?? ''], d: dm[2], y: dm[3] } : md ? { mon: MO[md[2] ?? ''], d: md[1], y: md[3] } : null;
  if (day && day.mon && day.d && day.y) {
    out.rdFull = day.y + '-' + day.mon + '-' + day.d.padStart(2, '0');
  }

  if (d['is_free'] === true) {
    out.p = 0;
    out.o = 0;
  } else {
    const po = rec(d['price_overview']);
    const final = typeof po?.['final'] === 'number' ? (po['final'] as number) : null;
    const initial = typeof po?.['initial'] === 'number' ? (po['initial'] as number) : 0;
    if (final !== null) {
      out.p = final / 100;
      out.o = initial > final ? initial / 100 : 0;
    }
  }

  const genres = Array.isArray(d['genres']) ? (d['genres'] as unknown[]) : [];
  out.ea = genres.some((x) => {
    const g = rec(x);
    return !!g && (String(g['id']) === '70' || /early access/i.test(String(g['description'] ?? '')));
  });

  const demos = Array.isArray(d['demos']) ? (d['demos'] as unknown[]) : [];
  out.demo = demos.length > 0;
  const first = rec(demos[0]);
  if (out.demo && first) {
    const aid = typeof first['appid'] === 'number' ? first['appid'] : Number(first['id']);
    if (Number.isSafeInteger(aid) && aid > 0) out.dAid = aid;
  }
  return out;
}

// ---------------------------------------------------------------------------
// live player counts — the display-only overlay
// ---------------------------------------------------------------------------

/**
 * How many appids one `POST /api/steam/ccu` may carry.
 *
 * A CONTRACT, NOT A TUNING KNOB: a 13th appid is a BAD_REQUEST rather than a
 * truncation, so raising this alone turns every overlay into an error envelope.
 * `worker/src/steam.ts#CCU_MAX` owns the number and this mirrors it; the reason
 * is written out in `worker/src/handlers/ccu.ts` — GetNumberOfCurrentPlayers
 * takes ONE appid per call, so unlike the 50-appid `items` batch this route's
 * cost scales with the request, at 12 reads + 12 fetches + 12 writes against
 * the free plan's 50-subrequest budget per invocation.
 */
export const CCU_MAX_BATCH = 12;

/**
 * `{ "<appid>": players | null }` — the overlay's answer for one batch.
 *
 * THREE STATES, and the difference between them is the whole point:
 *
 *   a number   Steam answered with a count. `0` is one of these: a real
 *              measurement saying nobody is in the game right now.
 *   `null`     we asked and Steam had no count for that appid.
 *   ABSENT     we never got an answer — the request failed, or the appid was
 *              never asked about. A caller must leave whatever it was already
 *              showing alone rather than rendering a zero it was not told.
 */
export type CcuMap = Record<string, number | null>;

/**
 * `POST /api/steam/ccu` — live player counts for the appids on screen.
 *
 * DISPLAY ONLY, and structurally so: the result is a plain map that goes to a
 * view, never to `saveFix`, `upsert_steam_facts` or any other Supabase write.
 * The Worker holds no service role and could not persist this if it wanted to;
 * client-authored CCU writes are exactly what the sweep replaces. The stored
 * `steam_facts.ccu` a row already carries stays the authority for ranking and
 * for anything the server captioned.
 *
 * Chunking is the caller's problem solved once, here, since `CCU_MAX_BATCH` is
 * a hard rejection rather than a suggestion (`slices/steamFacts.ts#giDrain` is
 * the same shape for the assets batch). The chunks go SEQUENTIALLY: each one is
 * its own Worker invocation fanning out up to 12 upstream calls, and a
 * screenful fired in parallel would multiply that fan-out against Steam for a
 * cosmetic overlay. It is the one call on this client that can afford to wait.
 *
 * Never rejects. A failed chunk is skipped — its appids stay ABSENT from the
 * map, which is exactly the "we were not told" state the type documents — so
 * one bad round trip cannot cost the other chunks their answers or take a
 * render path down with it.
 */
export async function fetchLiveCcu(opts: SteamApiOptions, appids: number[]): Promise<CcuMap> {
  const ids = [...new Set(appids)].filter((a) => Number.isSafeInteger(a) && a > 0);
  const out: CcuMap = {};

  for (let i = 0; i < ids.length; i += CCU_MAX_BATCH) {
    const batch = ids.slice(i, i + CCU_MAX_BATCH);
    let payload: Record<string, unknown> | null;
    try {
      payload = rec(
        await steamApi<unknown>(opts, '/api/steam/ccu', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ appids: batch }),
        }),
      );
    } catch {
      // OFFLINE, an error envelope, a stale-if-error copy (§4.3 — a count that
      // is stale is not a LIVE count and `steamApi` throws on it). All three
      // mean the same thing to a caller: these appids stay unanswered.
      continue;
    }
    if (!payload) continue;

    // Read back only what was asked for, and only if it is one of the two
    // shapes the contract allows. Anything else — a string, an object, a
    // negative count — is dropped to ABSENT rather than coerced: a wrong live
    // number is worse than no live number, since the caption it feeds claims
    // to be true *now*.
    for (const appid of batch) {
      const v = payload[String(appid)];
      if (v === null) out[String(appid)] = null;
      else if (typeof v === 'number' && Number.isFinite(v) && v >= 0) out[String(appid)] = Math.trunc(v);
    }
  }

  return out;
}

/**
 * The transport `fetchLiveCcu` gets when nobody injected one.
 *
 * Same origin (`apiBase: ''`) because that is what both repositories run on in
 * production — neither is constructed with a base (`repo/index.ts`), and in dev
 * `vite.config.ts` proxies the same `/api` path at `wrangler dev`. There is no
 * second origin to get wrong.
 *
 * `fetchImpl` IS NULL OFF-BROWSER, and that is the one place this differs from
 * the repositories, which bind `globalThis.fetch` at construction because their
 * callers inject a double for it. Nothing injects one here, so binding Node's
 * fetch would have every store test in this repo firing a relative URL at no
 * origin. Off a browser `fetchLiveCcu` answers `{}` — the "we were not told"
 * state every caller already has to handle — instead of a network error nobody
 * asked for.
 */
export const sameOriginCcu = (): SteamApiOptions => ({
  apiBase: '',
  fetchImpl:
    typeof window === 'undefined' || typeof fetch !== 'function' ? null : fetch.bind(globalThis),
});

// ---------------------------------------------------------------------------
// carousel media — the same appdetails payload, the half `mapAppDetails` drops
// ---------------------------------------------------------------------------

/** Caps. Memory bound, not a Steam limit: a big app ships 20+ of each. */
export const MEDIA_MAX_MOVIES = 8;
export const MEDIA_MAX_SHOTS = 12;

/**
 * PORTING-NOTES P10-04. The trailer source is now READ from the payload rather
 * than built from the movie id.
 *
 * The first cut built `…/steam/apps/<movie id>/movie480.mp4`, on the strength of
 * that path still serving TF2 and the Hades II reels. It does — because those
 * predate Steam's DASH/HLS cutover. It 404s for everything encoded after it, and
 * because the carousel mounts the `<video>` with `preload="none"` the browser
 * never asked for the file until the user pressed play, so a dead trailer
 * removed itself from the strip mid-tap (the reported bug).
 *
 * `hls_h264` is on every `movies[]` entry old and new, so there is nothing left
 * to guess. It costs a media-source player: `hls.js` is the fifth runtime
 * package, deliberately (docs/PLAN.md §1).
 */
const HLS_KEY = 'hls_h264';

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/** Steam sometimes types an id as a number and sometimes as a numeric string. */
const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isSafeInteger(n) && n > 0 ? n : 0;
};

/**
 * appdetails `data` → the carousel's Steam half.
 *
 * Defensive by construction: the argument is `unknown` because it is an upstream
 * shape nobody here controls, every field is optional at runtime whatever the
 * docs claim, and a malformed entry is SKIPPED rather than allowed to poison the
 * array — one bad screenshot must not cost a game its trailers. The caps bound
 * what a single game can hold in a session-lifetime cache.
 *
 * Pure and total: it never throws and never returns null, so the caller has no
 * error branch to forget.
 */
export function mapAppMedia(data: unknown): GameMedia {
  const d = rec(data);
  if (!d) return { shots: [], movies: [] };

  const movies: SteamMovie[] = [];
  const rawMovies = Array.isArray(d['movies']) ? (d['movies'] as unknown[]) : [];
  for (const entry of rawMovies) {
    if (movies.length >= MEDIA_MAX_MOVIES) break;
    const m = rec(entry);
    if (!m) continue;
    const id = num(m['id']);
    // No id means no stable tile key. No playlist means nothing to play — and
    // unlike the built-mp4 era there is no fallback to guess at, so the entry is
    // skipped HERE rather than mounted and pruned under the user later.
    const hls = str(m[HLS_KEY]);
    if (!id || !hls) continue;
    movies.push({
      id,
      name: str(m['name']),
      poster: str(m['thumbnail']),
      hls,
    });
  }

  const shots: SteamShot[] = [];
  const rawShots = Array.isArray(d['screenshots']) ? (d['screenshots'] as unknown[]) : [];
  for (const entry of rawShots) {
    if (shots.length >= MEDIA_MAX_SHOTS) break;
    const s = rec(entry);
    if (!s) continue;
    const full = str(s['path_full']);
    const thumb = str(s['path_thumbnail']);
    // Either one alone is still a usable tile; neither is not.
    if (!full && !thumb) continue;
    shots.push({ thumb: thumb || full, full: full || thumb });
  }

  return { shots, movies };
}

/**
 * The "we asked and there is nothing" value. A factory, not a shared constant:
 * a frozen-looking module-level object with two arrays on it is exactly the
 * thing a later caller pushes into by accident.
 */
export const emptyMedia = (): GameMedia => ({ shots: [], movies: [] });

/**
 * `SteamFactPatch` → the `upsert_steam_facts` row shape (ARCHITECTURE §1.12).
 * Undefined fields are omitted so the RPC's `coalesce(excluded, stored)` keeps
 * whatever another source already filled in.
 *
 * SEVEN COLUMNS, all of them things one `appdetails` round trip actually
 * measured. The two keys that used to ride along and no longer do:
 *
 *   `review_pct`  DEAD FROM THE DAY IT WAS WRITTEN. `mapAppDetails` is the only
 *                 producer of a patch that reaches this function, and it never
 *                 sets `rp` — the review block is not in the payload the Worker
 *                 forwards — so the branch never once fired. It was a write the
 *                 client was *granted* and never used: all of the blast radius,
 *                 none of the value. `steam_facts.review_pct` belongs to the
 *                 pool jobs, which measure it properly.
 *   `source`      the client is not the authority on provenance. Every row it
 *                 sends came from appdetails by construction, so the key was the
 *                 browser asserting something the server can derive — and the
 *                 column is what a reader uses to decide whose number to trust.
 *
 * SAFE IN BOTH DEPLOY ORDERS, which is why it can change here first. The RPC as
 * deployed (migration 1100) defaults an absent `source` to `'appdetails'`, so
 * dropping the key changes nothing for the old function; migration 2909 narrows
 * the RPC to exactly the set below and IGNORES anything else rather than
 * raising, so the old bundle's extra keys change nothing for the new one.
 */
export function factPatchToRow(
  p: SteamFactPatch,
): Record<string, string | number | boolean | null> {
  const row: Record<string, string | number | boolean | null> = { appid: p.appid };
  // WRITEBACK_COLUMNS IS THE ALLOW-LIST, and it is the same table the extraction
  // reads. A field with `column: null` in the registry — `rp`, which migration
  // 2909 deliberately closed to client writes, and the display-only
  // `dev`/`ds`/`pcats` — has no entry here and therefore cannot reach the RPC,
  // whatever a caller puts on the patch. `upsert_steam_facts` has its own
  // allow-list in its SELECT list and would drop them anyway; this keeps the
  // client from asserting something it is not entitled to assert in the first
  // place, and keeps the two lists derived from one decision.
  const src = p as unknown as Record<string, unknown>;
  for (const [key, column] of Object.entries(WRITEBACK_COLUMNS)) {
    const v = src[key];
    // Primitives only, and the narrowing is not ceremony: every writeable field
    // is one by construction, so a non-primitive here means a registry entry
    // gained a column it has no business having (an array cannot be a
    // `steam_facts` scalar) and the safe reading is to send nothing.
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' || v === null) {
      row[column] = v;
    }
  }
  return row;
}
